import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

// @mui material components
import Card from '@mui/material/Card'
import Badge from '@mui/material/Badge'
import Modal from '@mui/material/Modal'

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'examples/Navbars/DashboardNavbar'
import Footer from 'examples/Footer'
import DataTable from 'examples/Tables/DataTable'
import MDAlert from 'components/MDAlert'
import { Tooltip, IconButton, CardContent, Typography } from '@mui/material'

import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'

import CrudService from 'services/cruds-service'
import { AbilityContext } from 'Can'
import { useAbility } from '@casl/react'
import { useNavigate } from 'react-router-dom'
import MDButton from 'components/MDButton'
import moment from 'moment'

function CampaignEventList() {
    let { state } = useLocation()
    const ability = useAbility(AbilityContext)
    const [data, setData] = useState([])
    const [tableData, setTableData] = useState([])
    const [notification, setNotification] = useState({
        value: false,
        text: '',
    })
    const [loader, setLoader] = useState(false)
    const navigate = useNavigate();

    useEffect(() => {
        ; (async () => {
            try {
                setLoader(true)
                const response = await CrudService.getAllCampaignEvent()
                setData(response.data)
                setLoader(false)
            } catch(error) {
                console.error('error', error)
                setLoader(false)
            }
        })()
        document.title = `Rivio | Campaign | Event`
    }, [])

    useEffect(() => {
        if (!state) return
        setNotification({
            value: state.value,
            text: state.text,
        })
    }, [state])

    useEffect(() => {
        setTableData(getRows(data))
    }, [data])

    useEffect(() => {
        if (notification.value === true) {
            let timer = setTimeout(() => {
                setNotification({
                    value: false,
                    text: '',
                })
            }, 5000)
        }
    }, [notification])

    const clickAddHandler = () => {
        navigate('/campaign/event/new')
    }

    const clickEditHandler = async (id) => {
        navigate(`/campaign/event/edit/${id}`)
    }

    const clickDeleteHandler = async (e, id) => {
        try {
            if (!confirm('Are you sure you want to delete this Event?')) {
                e.nativeEvent.stopImmediatePropagation()
            } else {
                setLoader(true)
                await CrudService.removeCampaignEvent(id)
                // the delete does not send a response
                // so I need to get again the categories to set it and this way the table gets updated -> it goes to the useEffect with data dependecy
                const response = await CrudService.getAllCampaignEvent()
                setData(response.data)
                setNotification({
                    value: true,
                    text: 'The Event has been successfully deleted',
                })
                setLoader(false)
            }
        } catch (err) {
            // it sends error is the API is associated with an API
            console.error(err)
            if (err.hasOwnProperty('msg')) {
                setNotification({
                    value: true,
                    text: err.msg,
                })
            }
            setLoader(false)
            return null
        }
    }

    const getRows = (info) => {
        let updatedInfo = info.map((row) => {

            return {
                id: row.event_id,
                uid: row.uid,
                company: row?.CampaignCompany?.company_name,
                event_type: row.event_type,
                event_notes: row.event_notes,
                spoke_name: row.spoke_name,
                contact_made: row.contact_made ? 'Yes' : 'No',
                unknown_contact: row.unknown_contact ? 'Yes' : 'No',
                entry_timestamp: row?.entry_timestamp ? moment(row?.entry_timestamp).format('MMMM DD, YYYY h:mm:ss A') : '',
                voicemail_timestamp: row?.voicemail_timestamp ? moment(row?.voicemail_timestamp).format('MMMM DD, YYYY h:mm:ss A') : '',
            }
        })
        return updatedInfo
    }

    const dataTableData = {
        columns: [
            { Header: 'ID', accessor: 'id' },
            { Header: 'UID', accessor: 'uid' },
            { Header: 'company', accessor: 'company', width: '15%' },
            { Header: 'Type', accessor: 'event_type' },
            { Header: 'Notes', accessor: 'event_notes', width: '20%' },
            { Header: 'Spoke Name', accessor: 'spoke_name' },
            { Header: 'Contact made', accessor: 'contact_made' },
            { Header: 'unknown contact', accessor: 'unknown_contact' },
            { Header: 'entry timestamp', accessor: 'entry_timestamp' },
            { Header: 'voicemail timestamp', accessor: 'voicemail_timestamp' },
            {
                Header: 'actions',
                disableSortBy: true,
                accessor: '',
                Cell: (info) => {
                    return (
                        <MDBox display="flex" alignItems="center">
                            {ability.can('edit', 'campaign') && (
                                <Tooltip title="Edit Campaign">
                                    <IconButton
                                        onClick={() =>
                                            clickEditHandler(info.cell.row.original.id)
                                        }
                                    >
                                        <MDTypography>
                                            <EditIcon />
                                        </MDTypography>
                                    </IconButton>
                                </Tooltip>
                            )}

                            {ability.can('delete', 'campaign') && (
                                <Tooltip title="Delete Campaign">
                                    <IconButton
                                        onClick={(e) =>
                                            clickDeleteHandler(e, info.cell.row.original.id)
                                        }
                                    >
                                        <MDTypography>
                                            <DeleteIcon />
                                        </MDTypography>
                                    </IconButton>
                                </Tooltip>
                            )}
                        </MDBox>
                    )
                },
            },
        ],

        rows: tableData,
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            {notification.value && (
                <MDAlert color="info" my="20px">
                    <MDTypography variant="body2" color="white">
                        {notification.text || ''}
                    </MDTypography>
                </MDAlert>
            )}
            <MDBox pt={1}>
                <MDBox >
                    <Card>
                        <MDBox
                            p={3}
                            lineHeight={1}
                            display="flex"
                            justifyContent="space-between"
                        >
                            <MDTypography variant="h5" fontWeight="medium">
                                Campaign Event
                            </MDTypography>
                            {ability.can('create', 'campaign') && (
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    size="small"
                                    type="submit"
                                    onClick={clickAddHandler}
                                >
                                    + Add Event
                                </MDButton>
                            )}
                        </MDBox>

                        <DataTable table={dataTableData} canSearch={true} fixedWidth={true} tableHeight="calc(100vh - 220px)" loader={loader}/>
                        {/* <MultiSelectDataTable rowData={getRows(data)} selectedRows={selectedRows} setSelectedRows={setSelectedRows}/> */}
                    </Card>
                </MDBox>
            </MDBox>
            <Footer />
        </DashboardLayout>
    )
}

export default CampaignEventList